import clsx from 'clsx'
import React, {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type PropsWithChildren,
  type ReactNode,
  useEffect,
  useRef,
} from 'react'

import {
  AmountField,
  FvButton,
  FvLinkButton,
  Icon,
  type IconProps,
  TextField,
} from '@fv/client-components'

import { removeUndefinedProps } from '@/utils/removeUndefinedProperties'

import { Help } from '../Help'
import { HelpHrefButton } from '../HelpHrefButton'
import { ShipperTooltip } from '../ShipperTooltip'
import { RadioField } from './RadioField'
import { SelectField } from './SelectField'
import { YesNoInput } from './YesNoInput'

type Props = {
  className?: string
  helpText?: ReactNode
  helpHref?: string
  label?: ReactNode
  locked?: boolean
  name: string
  required?: boolean
  labelClassName?: string
  hint?: ReactNode
}

export const InputGroupWrapper = ({
  children,
  className,
  helpText,
  helpHref,
  label,
  locked,
  name,
  labelClassName,
  required,
  hint,
}: PropsWithChildren<Props>) => {
  return (
    <div className={clsx('form-group', className)}>
      {label && (
        <label
          className={clsx(
            'inline-flex items-center whitespace-nowrap gap-x-1 mb-2',
            labelClassName,
            required && "after:content-['*'] after:text-fv-orange-bright",
          )}
          htmlFor={name}
        >
          {locked && (
            <Icon className="text-fv-orange" icon="lock" transform="shrink-1" />
          )}
          {label}
          {helpText && helpHref && (
            <HelpHrefButton href={helpHref} text={helpText} />
          )}
          {helpText && !helpHref && <Help>{helpText}</Help>}
        </label>
      )}

      <div
        className={clsx(
          'input-group flex relative',
          required &&
            !label &&
            'after:content-["*"] after:text-fv-orange-bright after:absolute after:top-1 after:right-2 after:z-10',
        )}
      >
        {children}
      </div>

      {hint}
    </div>
  )
}

interface InputGroupProps extends Omit<Props, 'name'> {
  endContent?: ReactNode
  startContent?: ReactNode
}

interface AmountGroup extends InputGroupProps {
  inputProps: ComponentPropsWithRef<typeof AmountField>
  inputType: 'amount'
}

interface RadioGroup extends InputGroupProps {
  inputProps: ComponentPropsWithoutRef<typeof RadioField>
  inputType: 'radio'
}

interface SelectGroup extends InputGroupProps {
  inputProps: ComponentPropsWithRef<typeof SelectField>
  inputType: 'select'
}

interface TextGroup extends InputGroupProps {
  inputProps: ComponentPropsWithRef<typeof TextField>
  inputType: 'text'
}
interface YesNoGroup extends InputGroupProps {
  inputProps: ComponentPropsWithRef<typeof YesNoInput>
  inputType: 'yes-no'
}

interface CustomGroup extends InputGroupProps {
  inputProps: ComponentPropsWithRef<typeof TextField>
  inputType: 'custom'
}

export const InputGroup = ({
  endContent,
  inputProps,
  inputType,
  locked,
  required,
  startContent,
  children,
  autoFocus,
  ...rest
}: PropsWithChildren<
  AmountGroup | RadioGroup | SelectGroup | TextGroup | CustomGroup | YesNoGroup
> & { autoFocus?: boolean }) => {
  let inputNode: ReactNode = children
  const ref = useRef<HTMLInputElement>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only
  useEffect(() => {
    if (autoFocus && ref?.current) {
      ref.current.focus()
    }
  }, [])

  const sharedProps = {
    disabled: locked || inputProps.disabled,
    required,
  }

  const wrapperProps = {
    ...rest,
    locked,
    name: inputProps.name,
    required,
  }
  const className = clsx('form-control truncate', inputProps.className)

  if (inputType === 'amount') {
    inputNode = (
      <AmountField
        ref={ref}
        {...sharedProps}
        {...removeUndefinedProps(inputProps)}
        className={className}
      />
    )
  }

  if (inputType === 'radio') {
    inputNode = (
      <RadioField
        {...sharedProps}
        {...removeUndefinedProps(inputProps)}
        className={className}
      />
    )
  }

  if (inputType === 'select') {
    inputNode = (
      <SelectField
        {...sharedProps}
        {...removeUndefinedProps(inputProps)}
        className={className}
      />
    )
  }

  if (inputType === 'text') {
    inputNode = (
      <TextField
        ref={ref}
        {...sharedProps}
        {...removeUndefinedProps(inputProps)}
        className={className}
      />
    )
  }

  if (inputType === 'yes-no') {
    inputNode = (
      <YesNoInput
        {...sharedProps}
        {...removeUndefinedProps(inputProps)}
        className={className}
      />
    )
  }

  return (
    <InputGroupWrapper {...wrapperProps}>
      {startContent}
      {inputNode}
      {endContent}
    </InputGroupWrapper>
  )
}

type InputAdornmentProps = PropsWithChildren<{
  className?: string
  icon?: IconProps['icon']
  iconClass?: string
  label?: string
  htmlFor?: string
  position?: 'start' | 'end'
  onClick?: () => void
}>

export const InputAdornment = ({
  children,
  className,
  htmlFor,
  icon,
  iconClass = 'text-fv-gray-dark',
  label,
  position = 'end',
  onClick,
}: InputAdornmentProps) => {
  const Wrapper = htmlFor ? 'label' : 'div'
  const containsButton = React.Children.toArray(children).some(
    child =>
      React.isValidElement(child) &&
      (child.type === FvButton || child.type === ShipperTooltip), // TODO fix this we need to stop checking the children array
  )
  const containsLinkButton = React.Children.toArray(children).some(
    child =>
      React.isValidElement(child) &&
      (child.type === FvLinkButton || child.type === ShipperTooltip), // TODO fix this we need to stop checking the children array
  )
  return (
    <Wrapper
      {...(htmlFor ? { htmlFor } : {})}
      className={clsx('flex', { 'cursor-pointer': !!onClick })}
      onClick={onClick}
    >
      <div
        className={clsx(
          className,
          containsLinkButton
            ? '[&>a]:flex [&>a]:h-[2.8rem] [&>a]:items-center [&>a]:border [&>a]:border-[#b3b3b3] [&>a]:bg-white [&>a]:px-[.6rem]'
            : !containsButton &&
                !containsLinkButton &&
                'flex items-center justify-center border border-[#b3b3b3] bg-white px-[.6rem]',
          position === 'start' && 'border-r-0',
          position === 'end' && 'border-l-0',
          containsButton &&
            `${
              position === 'start'
                ? '-mr-px'
                : position === 'end'
                  ? '-ml-px'
                  : ''
            }`,
          containsLinkButton &&
            `${
              position === 'start'
                ? '-mr-px'
                : position === 'end'
                  ? '-ml-px'
                  : ''
            }`,
        )}
      >
        {children}
        {icon && (
          <Icon icon={icon} className={clsx(iconClass, 'fa-width-auto')} />
        )}
        {label && <span>{label}</span>}
      </div>
    </Wrapper>
  )
}
